import { Box, Button, Dialog, DialogContent, DialogTitle, Divider } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import adminApi from "../../api/adminApi";
import type {
  LookupObjectReference,
  ObjectFieldValues,
  ObjectTypeToLookupReferences,
} from "../../api/types/objectTypes";
import useClientContext from "../../context/useClientContext";
import useGetObjectPageLayout from "../../hooks/useGetObjectPageLayout";
import FieldValuesContainer from "./FieldValuesContainer";
import type { FieldValuesState } from "./FieldValuesContext";
import { isAnyRequiredFieldEmpty } from "./fieldValuesManagement";

const createObject = withErrorHandling(adminApi.createObject);

interface Props {
  objectType: string;
  entityTypeCode?: string;
  onClose: () => void;
  onCreated: (newObject: ObjectFieldValues) => void;
}

const NewObjectDialog = ({ objectType, entityTypeCode, onClose, onCreated }: Props) => {
  const { hasAnyPermission } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);

  const [fieldValuesState, setFieldValuesState] = useState<FieldValuesState>({
    fieldValues: {},
    isValid: true,
    fieldEditState: "viewing",
  });

  const getObjectDefinition = useCallback(() => adminApi.getObjectDefinition(objectType), [objectType]);

  const [definition, fetchDefinitionError] = useFetch(getObjectDefinition);

  const getCardLayout = useGetObjectPageLayout(objectType);

  const [cardLayout, fetchCardLayoutError] = useFetch(getCardLayout);

  const hasPermissionsToManageFields = useMemo(
    () => (definition ? hasAnyPermission(definition.objectPermissions.objectDataWritePermissions) : false),
    [hasAnyPermission, definition]
  );

  const getLookupOptions = useCallback(
    () =>
      hasPermissionsToManageFields && definition?.objectClassDefinition.fields.some((f) => f.type === "Lookup")
        ? adminApi.getLookupOptions(objectType)
        : Promise.resolve(createApiResponse({})),
    [hasPermissionsToManageFields, definition?.objectClassDefinition.fields, objectType]
  );

  const [
    lookupOptionsByObjectType,
    lookupOptionsError,
    { isFetching: isFetchingLookupOptions, setData: setLookupOptions },
  ] = useFetch(getLookupOptions);

  const fetchError = fetchDefinitionError || fetchCardLayoutError || lookupOptionsError;
  if (fetchError) {
    logError(fetchError, "[NewObjectDialog] fetch");
  }

  const handleFieldValuesStateChange = useCallback((stateUpdate: Partial<FieldValuesState>) => {
    setFieldValuesState((prev) => ({ ...prev, ...stateUpdate }));
  }, []);

  const handleAddLookupOption = useCallback(
    (objectType: string, lookupObjectReference: LookupObjectReference) => {
      setLookupOptions((prev) => {
        if (!prev) {
          return { [objectType]: [lookupObjectReference] };
        }

        const updatedOptions: ObjectTypeToLookupReferences = { ...prev };
        if (!updatedOptions[objectType]) {
          updatedOptions[objectType] = [];
        }

        updatedOptions[objectType].push(lookupObjectReference);
        return updatedOptions;
      });
    },
    [setLookupOptions]
  );

  const handleCreate = async () => {
    setSaving(true);

    const [object, error] = await createObject(objectType, {
      fieldValuesUserInput: fieldValuesState.fieldValues,
      entityTypeCode,
    });

    await wait(500);

    setSaving(false);

    if (error) {
      logError(error, "[NewObjectDialog] createObject");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Object created successfully");
    onCreated(object);
  };

  const isDataLoaded = definition !== undefined && cardLayout !== undefined && !isFetchingLookupOptions;

  const isValid =
    definition !== undefined &&
    fieldValuesState.fieldEditState !== "editing" &&
    fieldValuesState.isValid &&
    !isAnyRequiredFieldEmpty(definition.objectClassDefinition.fields, fieldValuesState.fieldValues);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create {definition?.objectClassDefinition.objectName ?? ""}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent>
        {isDataLoaded && (
          <Box sx={{ flex: 1, overflow: "auto", px: 2.5, pb: 2 }}>
            <FieldValuesContainer
              objectType={definition.objectClassDefinition.objectType}
              businessCentralType={definition.objectClassDefinition.businessCentralType}
              fields={definition.objectClassDefinition.fields.filter((f) => !f.attributes.includes("ReadOnly"))}
              cardLayout={cardLayout}
              initialFieldValues={{}}
              lookupOptionsByObjectType={lookupOptionsByObjectType}
              onAddLookupOption={handleAddLookupOption}
              allowEditing={hasPermissionsToManageFields}
              fieldValuesState={fieldValuesState}
              onFieldValuesStateChange={handleFieldValuesStateChange}
              showFieldValueBorders
            />
          </Box>
        )}

        {!isDataLoaded && <Box sx={{ height: "8rem" }}>{fetchError ? <DataLoadingFailed /> : <InlineLoader />}</Box>}
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" onClick={handleCreate} loading={saving} disabled={!isValid}>
          Create
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default NewObjectDialog;
